import React from "react";
import { ListItem, List, Typography, Box } from "@mui/material";
import PropTypes from 'prop-types';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { smoothScrollToElement } from '../../utils/scrollUtils';

/**
 * Enhanced NavigationMenuItem with improved animations and section handling
 * Based on the OctopusSync template but adapted for SpinnakerAlgo
 */
const NavigationMenuItem = ({
    item,
    expandedMenu,
    handleMenuToggle,
    location,
    activeSection,
    textColor = '#000000',
    activeColor = '#6511af',
    startPageTransition,
    navigate,
    pageSectionRefsContainer
}) => {
    const localNavigate = useNavigate();
    const isHomePage = location.pathname === '/';
    const itemBasePath = item.path.split('#')[0];
    const isParentActive = location.pathname === itemBasePath || 
                           (itemBasePath !== '/' && location.pathname.startsWith(`${itemBasePath}/`));
    
    // Keep dropdown open if we're on the parent page or if manually expanded
    const shouldShowDropdown = isParentActive || expandedMenu === item.title;

    const handleNavigate = (targetPath, sectionId = null) => {
        // Extract base path and hash from targetPath
        const [basePath, hash] = targetPath.split('#');
        // Use hash from path if present, otherwise use sectionId
        const finalSectionId = hash || sectionId;
        
        const normalizedBasePath = basePath !== "/" && basePath.endsWith("/") ? basePath.slice(0, -1) : basePath;
        const normalizedCurrentPath = location.pathname !== "/" && location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname;
        
        // If same page, just scroll to section and update hash
        if (normalizedBasePath === normalizedCurrentPath && finalSectionId) {
            // Update URL with hash
            localNavigate(`${basePath}#${finalSectionId}`);
            // Scroll after a brief delay to ensure DOM is updated
            setTimeout(() => {
                smoothScrollToElement(finalSectionId, 100);
            }, 50);
            return;
        }
        
        // Cross-page navigation
        if (finalSectionId) {
            localNavigate(`${basePath}#${finalSectionId}`);
        } else {
            localNavigate(basePath);
        }
    };

    if (item.subItems && item.subItems.length > 0) {
        return (
            <div>
                <ListItem
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: '8px 16px',
                        fontWeight: 300,
                        fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
                        textAlign: 'left',
                        color: isParentActive ? activeColor : textColor,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            color: activeColor,
                            cursor: 'pointer',
                            backgroundColor: isHomePage 
                                ? 'rgba(255, 255, 255, 0.05)' 
                                : 'rgba(0, 0, 0, 0.03)'
                        },
                        borderRadius: 1,
                        marginY: 0.5
                    }}
                >
                    {/* Expand/Collapse Icon */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            marginRight: 1,
                            opacity: shouldShowDropdown ? 1 : 0.7,
                            '&:hover': { opacity: 1 }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuToggle(item.title);
                        }}
                    >
                        <ExpandMoreIcon
                            sx={{
                                transform: shouldShowDropdown 
                                    ? 'rotate(0deg)' 
                                    : 'rotate(-90deg)',
                                transition: 'transform 0.3s ease',
                                color: 'inherit',
                                fontSize: '1.2rem'
                            }}
                        />
                    </Box>

                    {/* Main Item Title */}
                    <Typography
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            fontSize: 'inherit',
                            cursor: 'pointer',
                            fontWeight: 'inherit',
                            flex: 1,
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {item.title}
                    </Typography>
                </ListItem>

                {/* Animated Submenu */}
                <AnimatePresence>
                    {shouldShowDropdown && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ 
                                height: 'auto', 
                                opacity: 1,
                                transition: {
                                    duration: 0.3,
                                    ease: [0.4, 0.0, 0.2, 1]
                                }
                            }}
                            exit={{ 
                                height: 0, 
                                opacity: 0,
                                transition: {
                                    duration: 0.2,
                                    ease: [0.4, 0.0, 0.2, 1]
                                }
                            }}
                            style={{ overflow: 'hidden' }}
                        >
                            <List sx={{ paddingLeft: 2, paddingY: 0 }}>
                                {item.subItems.map((subItem, index) => {
                                    const sectionId = subItem.path.split('#')[1];
                                    const isSubItemActive = activeSection === sectionId;
                                    
                                    return (
                                        <motion.div
                                            key={subItem.title}
                                            initial={{ x: -10, opacity: 0 }}
                                            animate={{ 
                                                x: 0, 
                                                opacity: 1,
                                                transition: {
                                                    delay: index * 0.05,
                                                    duration: 0.2
                                                }
                                            }}
                                            exit={{ 
                                                x: -10, 
                                                opacity: 0,
                                                transition: {
                                                    duration: 0.1
                                                }
                                            }}
                                        >
                                            <ListItem
                                                onClick={() => handleNavigate(subItem.path, sectionId)}
                                                sx={{
                                                    padding: '6px 12px',
                                                    fontSize: { md: '0.9rem', lg: '0.95rem' },
                                                    fontWeight: 300,
                                                    color: isSubItemActive ? activeColor : textColor,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    borderRadius: 1,
                                                    marginY: 0.25,
                                                    '&:hover': {
                                                        color: activeColor,
                                                        backgroundColor: isHomePage 
                                                            ? 'rgba(255, 255, 255, 0.03)' 
                                                            : 'rgba(0, 0, 0, 0.02)',
                                                        paddingLeft: '16px'
                                                    },
                                                    ...(isSubItemActive && {
                                                        backgroundColor: isHomePage 
                                                            ? 'rgba(255, 255, 255, 0.08)' 
                                                            : 'rgba(0, 0, 0, 0.05)',
                                                        fontWeight: 400
                                                    })
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 'inherit',
                                                        fontWeight: 'inherit',
                                                        color: 'inherit'
                                                    }}
                                                >
                                                    {subItem.title}
                                                </Typography>
                                            </ListItem>
                                        </motion.div>
                                    );
                                })}
                            </List>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    // Simple menu item without dropdown
    return (
        <ListItem
            onClick={() => handleNavigate(item.path)}
            sx={{
                padding: '8px 16px',
                fontSize: { md: '1rem', lg: '1.05rem', xl: '1.1rem' },
                fontWeight: 300,
                textAlign: 'left',
                color: isParentActive ? activeColor : textColor,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderRadius: 1,
                marginY: 0.5,
                '&:hover': {
                    color: activeColor,
                    backgroundColor: isHomePage 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.03)',
                    transform: 'translateX(4px)'
                },
                ...(isParentActive && {
                    backgroundColor: isHomePage 
                        ? 'rgba(255, 255, 255, 0.08)' 
                        : 'rgba(0, 0, 0, 0.05)',
                    fontWeight: 400
                })
            }}
        >
            <Typography
                sx={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    color: 'inherit'
                }}
            >
                {item.title}
            </Typography>
        </ListItem>
    );
};

NavigationMenuItem.propTypes = {
    item: PropTypes.object.isRequired,
    expandedMenu: PropTypes.string,
    handleMenuToggle: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    activeSection: PropTypes.string,
    textColor: PropTypes.string,
    activeColor: PropTypes.string,
    navigate: PropTypes.func.isRequired,
    pageSectionRefsContainer: PropTypes.object
};

export default NavigationMenuItem;
